import { fetcher, useFetch } from '@chipinside/fetcher'
import { Moment } from '@chipinside/frontend'
import { Dialog, IconButton } from '@mui/material'
import { isEmpty } from 'lodash'
import { useMemo, useState } from 'react'

import { Icon } from '#/components/Icon'
import { CircularLoader } from '#/components/Loader/Circular'
import { ReleaseNoteContent } from '#/components/ReleaseNotes/content'
import { useFarmSlug } from '#/store/ducks/farm'
import { endpoint } from '#/store/services/endpoints'
import { isDialogsDisabled } from '#/utils'
import { listeners } from '#/utils/listeners'
import tryCache from '#/utils/tryCache'

type ReleaseNotesProps = {
  id: string
  release_title: string
  release_date: string
  published: boolean
  created_at: string
  updated_at: string
  creator_id: string
  deleted_at: string
  release_note: string
}

type ReleaseViewedProps = {
  id: string
  slug: string
  viewed_last_release: {
    viewed: boolean
    updated_at: string | null
  } | null
}

const TC_VIEWED_KEY = 'viewed-last-rn'

const ReleaseNote = () => {
  const farmSlug = useFarmSlug()

  const [open, setOpen] = useState(false)

  /**
   * Tenta pegar do cache se o usuário já viu a última release
   */
  const cachedViewedData = tryCache.get(TC_VIEWED_KEY)

  const controller = useMemo(() => {
    return {
      last: endpoint.system.release_notes.last(),
      viewed_last: endpoint.system.release_notes.viewed_last(),
    }
  }, [])

  /**
   * Busca id e se o usuário já viu a última release
   */
  useFetch<ReleaseViewedProps>({
    controller: !isDialogsDisabled && isEmpty(cachedViewedData) ? controller.viewed_last : null,
    swrOptions: {
      onSuccess: ({ data }) => {
        /**
         * Adiciona no localstorage os dados se o usuário já viu a última release
         */
        tryCache.set(TC_VIEWED_KEY, data, 10)
        /**
         * Se é null o usuário nunca viu a última release
         */
        if (data.viewed_last_release === null) {
          setOpen(true)
          /**
           * Se ja viu mas clicou em ver depois, adiciona 24 horas para exibir novamente
           */
        } else if (data.viewed_last_release?.viewed === false) {
          /**
           * Se já passou 24 horas desde a última vez que foi exibido, mostra novamente
           */
          if (Moment.diff('now', data.viewed_last_release?.updated_at, 'h') > 24) {
            setOpen(true)
          }
        }
      },
    },
  })

  const { data, isLoading } = useFetch<ReleaseNotesProps>({
    controller: open ? controller.last : null,
  })

  function handleOnClick(type?: string) {
    if (data?.id) {
      const controller =
        type === 'read-later'
          ? endpoint.system.release_notes.read_later({ note: data.id })
          : endpoint.system.release_notes.view({ note: data.id })

      fetcher({ controller, method: 'put' })
        .then(() => {
          //Delete drawer header cache
          tryCache.clear(`notification-list-${farmSlug}-header`)
          //Delete drawer tab release content
          tryCache.clear(`notification-list-release`)
        })
        .catch(error => console.error(error))
    }
    /**
     * Atualiza o cache local para não exibir novamente a notificação nas próximas 24 horas
     */
    tryCache.set(
      TC_VIEWED_KEY,
      {
        viewed_last_release: {
          updated_at: new Date(),
          viewed: type !== 'read-later',
        },
      },
      1440,
    )

    window.dispatchEvent(new Event(listeners.close_release_note))
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={() => handleOnClick()} fullWidth maxWidth="md" id="release-notes">
      <IconButton
        aria-label="close"
        onClick={() => handleOnClick()}
        sx={theme => ({
          color: theme.palette.grey[500],
          position: 'absolute',
          right: 8,
          top: 8,
        })}
      >
        <Icon size="large" name="mui-close" />
      </IconButton>
      {isLoading ? (
        <CircularLoader containerStyle={{ padding: 40 }} />
      ) : (
        <ReleaseNoteContent data={data} handleOnClick={handleOnClick} />
      )}
    </Dialog>
  )
}

export { ReleaseNote }
